import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Camera } from 'components/UIElements'
import { formatInnerHTML } from 'utils/misc'
import { timeNow, getCurrentUTCDateTime } from 'utils/time'
import { closeIcon, questionCircleIcon } from 'assets/assets'
import SurveyQuestion from '../SurveyQuestion'
import QuestionTitleImage from '../QuestionTitleImage'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
} from '../../utils/instrumentUtils'
import { restoreImageCaptureState } from '../../utils/imageCaptureUpload'

const buildImageCaptureSubmission = (
  props,
  imagesList,
  capturesObj,
  timeAnsweredVal,
) => {
  const { id, question } = props
  const { label, type, attributes } = question
  const { required } = attributes
  const filtered = imagesList.filter((image) => image !== null)
  const answer = filtered.map((image) => ({ value: image }))
  const submission = {
    id,
    label,
    name: id,
    type,
    required,
    state: filtered.length > 0 ? 'ANSWERED' : 'SKIPPED',
    answer,
    imageFiles: capturesObj,
  }
  if (filtered.length) submission.time_answered = timeAnsweredVal
  return submission
}

const persistImageCaptureSubmission = (
  props,
  imagesList,
  capturesObj,
  timeAnsweredVal,
) => {
  try {
    const { webSurvey, completeQuestion } = props
    if (typeof completeQuestion !== 'function' || !webSurvey) return
    completeQuestion(
      buildImageCaptureSubmission(
        props,
        imagesList,
        capturesObj,
        timeAnsweredVal,
      ),
      webSurvey.order?.[0],
    )
  } catch (e) {
    console.warn('ImageCapture autosave failed', { error: e })
  }
}

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

const toPersistableImageUrl = async ({ file, fileUrl, dataUrl }) => {
  if (typeof dataUrl === 'string' && dataUrl.startsWith('data:image')) {
    return dataUrl
  }
  if (file instanceof Blob) {
    return blobToDataUrl(file)
  }
  if (typeof fileUrl === 'string' && fileUrl.startsWith('data:image')) {
    return fileUrl
  }
  if (typeof fileUrl === 'string' && fileUrl.startsWith('blob:')) {
    const blob = await fetch(fileUrl).then((res) => res.blob())
    return blobToDataUrl(blob)
  }
  return null
}

const SampleImagesViewPage = (props) => {
  const {
    closeModal,
    id,
    imagesList,
    imagesSrc,
    strings,
    imageOrder = 0,
  } = props
  const [currentImageOrder, setCurrentImageOrder] = useState(0)
  const currentImageSrc = imagesSrc[imagesList[currentImageOrder]]

  useEffect(() => {
    setCurrentImageOrder(imageOrder)
  }, [imageOrder])

  return (
    <div className='flexed column center-justified'>
      <img
        src={currentImageSrc}
        alt={`${id}-sample-${imagesList[currentImageOrder]}`}
        prop={`this-is-placeholder-${currentImageOrder}`}
      />
      <span>
        {currentImageOrder + 1}/{imagesList.length}
      </span>
      <div className='position-relative'>
        <Button
          link
          className='close-sample-modal flexed column'
          children={
            <>
              {closeIcon()}
              <span>{strings.close || 'Close'}</span>
            </>
          }
          onClick={closeModal}
        />
        {currentImageOrder !== 0 && (
          <Button
            link
            icon='fas fa-angle-left'
            className='back-button flexed'
            content={strings.back}
            onClick={() => {
              const pageNumber = currentImageOrder - 1
              setCurrentImageOrder(pageNumber <= 2 ? pageNumber : 0)
            }}
          />
        )}
        {imagesList.length > 1 &&
          currentImageOrder !== imagesList.length - 1 && (
            <Button
              link
              icon='fas fa-angle-right'
              iconAfter
              className='next-button flexed'
              content={strings.next_btn}
              onClick={() => {
                const pageNumber = currentImageOrder + 1
                setCurrentImageOrder(pageNumber <= 2 ? pageNumber : 0)
              }}
            />
          )}
      </div>
    </div>
  )
}

const ImageCapture = (props) => {
  const {
    className,
    closeModal,
    completeQuestion,
    deleteStagedFile,
    id,
    nextQ,
    nextQId,
    openModal,
    params,
    participantId,
    question,
    renderBackButton,
    renderCameraHelpModal,
    renderDeleteConfirmationModal,
    setAllRequiredAnswered,
    strings,
    submissionSurvey,
    submissionLoading,
    webSurvey,
    isConsent,
    isSibling,
    isLastQuestion,
    reviewFunctionalitySupported,
    forwardedRef,
  } = props
  const {
    attributes,
    comments,
    imageSrc,
    label,
    previewImagesSrc,
    preview_images,
    type,
    camera_tip,
  } = question

  const restoredImageState = restoreImageCaptureState(props)
  const [imageCaptures, setImageCaptures] = useState(
    restoredImageState.captures,
  )
  const [images, setImages] = useState(restoredImageState.images)
  const [timeAnswered, setTimeAnswered] = useState(
    restoredImageState.timeAnswered,
  )
  const filteredImages = images.filter((image) => image !== null)

  useEffect(() => {
    const storedAnswerNames = (props.storedAnswer || [])
      .map((item) => item?.value)
      .filter(Boolean)
    if (!storedAnswerNames.length && !images.some(Boolean)) return
    persistImageCaptureSubmission(
      props,
      images,
      imageCaptures,
      images.some(Boolean) ? timeAnswered : null,
    )
    // Hydrate Redux/localStorage once on resume so Finish can upload staged photos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { required, max_photo_limit: maxPhotoLimit } = attributes
  const _className = className ? ` ${className}` : ''

  const onCompleteImageCaptureQuestion = () => {
    const submission = buildImageCaptureSubmission(
      props,
      images,
      imageCaptures,
      timeAnswered,
    )
    const currentAnswer = { [id]: submission.answer }
    const shouldRenderNextQ = getShouldRenderNextQuestion({
      isConsent,
      isLastQuestion,
      isSibling,
    })
    checkLogicAndComplete(
      webSurvey,
      id,
      submissionSurvey,
      currentAnswer,
      nextQId,
      nextQ,
      completeQuestion,
      submission,
      reviewFunctionalitySupported,
      shouldRenderNextQ,
    )
  }

  const renderSampleImages = () => {
    return preview_images.map((imageId, idx) => (
      <Button
        link
        key={imageId}
        content={<img src={previewImagesSrc[imageId]} alt='place-holder' />}
        onClick={() =>
          openModal({
            className: 'view-sample-modal',
            content: (
              <SampleImagesViewPage
                closeModal={closeModal}
                id={id}
                imagesList={preview_images}
                imagesSrc={previewImagesSrc}
                strings={strings}
                imageOrder={idx}
              />
            ),
            noButtons: true,
            closeX: false,
            timesCloseX: false,
          })
        }
      />
    ))
  }

  const captureImage = ({ fileUrl, imageIdx, imageType, file, dataUrl }) => {
    setAllRequiredAnswered(false, id, [])

    const newImages = images.slice()
    const newImageCaptures = { ...imageCaptures }

    const fileNameToDel = newImages[imageIdx]
    if (fileNameToDel) {
      delete newImageCaptures[fileNameToDel]
      deleteStagedFile(fileNameToDel)
    }
    if (!fileUrl) {
      newImages[imageIdx] = null
      setImages(newImages)
      deleteStagedFile(fileNameToDel)
      setImageCaptures(newImageCaptures)
      persistImageCaptureSubmission(
        props,
        newImages,
        newImageCaptures,
        newImages.some(Boolean) ? timeAnswered : null,
      )
      return
    }

    const { studyId, instrumentId } = params
    const mimeType = file?.type || imageType || 'image/jpeg'
    const extension = mimeType.split('/')[1] || 'jpeg'
    const fileName = `${studyId}_${participantId}_${instrumentId}_${id}_${timeNow()}.${extension}`
    const answeredAt = getCurrentUTCDateTime()
    newImages[imageIdx] = fileName
    const previewUrl = dataUrl || fileUrl
    const nextCaptures = { ...newImageCaptures, [fileName]: previewUrl }
    setTimeAnswered(answeredAt)
    setImages(newImages)
    setImageCaptures(nextCaptures)
    persistImageCaptureSubmission(props, newImages, nextCaptures, answeredAt)

    toPersistableImageUrl({ file, fileUrl, dataUrl }).then((persistableUrl) => {
      if (!persistableUrl || persistableUrl === previewUrl) return
      const persistedCaptures = { ...nextCaptures, [fileName]: persistableUrl }
      setImageCaptures(persistedCaptures)
      persistImageCaptureSubmission(
        props,
        newImages,
        persistedCaptures,
        answeredAt,
      )
    })
  }

  const renderCamera = (imageIdx) => (
    <Camera
      content={strings.add_photo || 'Add photo'}
      icon='fas fa-plus'
      key={`image-upload-button-${imageIdx}`}
      strings={strings}
      openModal={openModal}
      closeModal={closeModal}
      isEditable
      initialImage={imageCaptures[images[imageIdx]]}
      onUpdate={(data) => {
        captureImage({
          fileUrl: data?.fileUrl,
          imageIdx,
          imageType: data?.imageType,
          file: data?.file,
          dataUrl: data?.dataUrl,
        })
      }}
      deletePermission={(onConfirm) => renderDeleteConfirmationModal(onConfirm)}
      cameraTip={camera_tip}
    />
  )

  const renderUploadButtons = () => {
    const imageButtons = []
    images.forEach((_image, index) => {
      imageButtons.push(renderCamera(index))
    })
    // Add an additional button if all slots are filled
    if (
      images.filter((val) => val).length === images.length &&
      images.length < maxPhotoLimit
    ) {
      imageButtons.push(renderCamera(images.length))
    }
    return imageButtons
  }

  return (
    <SurveyQuestion id={id}>
      <Button
        className='cam-permission-help'
        noStyling
        onClick={renderCameraHelpModal}
        content={questionCircleIcon()}
      />
      <MakeScrollable>
        <div
          className={`survey-question image-capture${_className}`}
          ref={forwardedRef}>
          <p className='question-label'>
            <span
              dangerouslySetInnerHTML={{ __html: formatInnerHTML(label) }}
            />
            <QuestionTitleImage imageSrc={imageSrc} questionId={id} />
          </p>
          <div className='image-content'>
            {preview_images && (
              <div className='flexed column start-aligned sample-images'>
                <Button
                  link
                  icon='fas fa-search-plus'
                  content={strings.sample_images || 'Sample image(s)'}
                  onClick={() =>
                    openModal({
                      className: 'view-sample-modal',
                      content: (
                        <SampleImagesViewPage
                          closeModal={closeModal}
                          id={id}
                          imagesList={preview_images}
                          imagesSrc={previewImagesSrc}
                          strings={strings}
                        />
                      ),
                      noButtons: true,
                      closeX: false,
                      timesCloseX: false,
                    })
                  }
                />
                <div className='flexed'>{renderSampleImages()}</div>
              </div>
            )}
            <div className='upload-buttons flexed start-justified'>
              {renderUploadButtons()}
            </div>
          </div>
          <div className='bottom-elements'>
            {comments && (
              <div className='instructions flexed column start-justified start-aligned'>
                <span>
                  {strings.instructions_comments || 'Instructions/Comments'}
                </span>
                <p>{comments}</p>
              </div>
            )}
            {!isSibling && (
              <QuestionFooter
                hideComments
                completed={filteredImages.length > 0}
                id={id}
                isConsent={isConsent}
                isLastQuestion={isLastQuestion}
                loading={submissionLoading}
                onComplete={() => onCompleteImageCaptureQuestion()}
                question={question}
                renderBackButton={renderBackButton}
                strings={strings}
                submissionSurvey={submissionSurvey}
                webSurvey={webSurvey}
              />
            )}
          </div>
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
}

const ForwardedImageCapture = React.forwardRef((props, ref) => (
  <ImageCapture {...props} forwardedRef={ref} />
))

export default ForwardedImageCapture

ImageCapture.propTypes = {
  className: PropTypes.string,
  closeModal: PropTypes.func,
  completeQuestion: PropTypes.func,
  deleteStagedFile: PropTypes.func,
  id: PropTypes.string,
  openModal: PropTypes.func,
  params: PropTypes.shape({
    instrumentId: PropTypes.string,
    studyId: PropTypes.string,
  }),
  question: PropTypes.shape({
    attributes: PropTypes.shape({
      required: PropTypes.bool,
    }),
    comments: PropTypes.string,
    imageSrc: PropTypes.string,
    label: PropTypes.string,
    preview_images: PropTypes.arrayOf(PropTypes.string),
    previewImagesSrc: PropTypes.object,
    type: PropTypes.string,
  }),
  renderBackButton: PropTypes.func,
  renderCameraHelpModal: PropTypes.func,
  renderDeleteConfirmationModal: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
}
